import type { LogEntry } from '../types';
import { fileExists, parseLogLine } from '../utils';
import { readFileReverse } from './file-reader';

/**
 * Capability trace builder for aggregating capability execution logs
 */
interface CapabilityTraceBuilder {
  traceId: string;
  capabilityId: string;
  pluginKey?: string;
  action?: string;
  startTime?: string;
  endTime?: string;
  durationMs?: number;
  status?: 'success' | 'failed';
  input?: string;
  output?: string;
  error?: { message: string };
  hasCompleted: boolean;
  hasStartEntry: boolean;
}

/**
 * Capability trace item
 */
export interface CapabilityTraceItem {
  traceId: string;
  capabilityId: string;
  pluginKey?: string;
  action?: string;
  startTime?: string;
  endTime?: string;
  durationMs?: number;
  status: 'success' | 'failed';
  input?: string;
  output?: string;
  error?: { message: string };
}

/**
 * Response for capability trace list
 */
export interface CapabilityTraceListResponse {
  capabilityId: string;
  totalTraces: number;
  traces: CapabilityTraceItem[];
}

/**
 * Read capability trace list from server.log
 * Filter by capability_id field in log entries
 * Returns only input and output entries for each trace
 */
export async function readCapabilityTraceList(
  filePath: string,
  capabilityId: string,
  limit?: number,
): Promise<CapabilityTraceListResponse | undefined> {
  if (!(await fileExists(filePath))) {
    return undefined;
  }

  const config = {
    chunkSize: 64 * 1024,
  };

  const builders = new Map<string, CapabilityTraceBuilder>();
  const completedTraces: CapabilityTraceBuilder[] = [];

  const createCapabilityTraceBuilder = (traceId: string, capId: string): CapabilityTraceBuilder => ({
    traceId,
    capabilityId: capId,
    hasCompleted: false,
    hasStartEntry: false,
  });

  const shouldIncludeInCompletedTraces = (builder: CapabilityTraceBuilder): boolean => {
    const alreadyAdded = completedTraces.some(trace => trace.traceId === builder.traceId);
    if (alreadyAdded) {
      return false;
    }
    return builder.capabilityId === capabilityId;
  };

  const updateBuilderMetadata = (builder: CapabilityTraceBuilder, entry: LogEntry): void => {
    if (entry.plugin_key && !builder.pluginKey) {
      builder.pluginKey = String(entry.plugin_key);
    }
    if (entry.action && !builder.action) {
      builder.action = String(entry.action);
    }

    const message = entry.message || '';

    // Check if this is the start of capability execution (入参日志)
    // 从独立字段读取 input
    if (message.includes('Executing capability') && !builder.hasStartEntry) {
      builder.hasStartEntry = true;
      builder.startTime = entry.time;
      if (entry.input) {
        builder.input = String(entry.input);
      }
    }

    // Check if this is successful completion (结果日志)
    // 从独立字段读取 output, duration_ms
    if (message.includes('executed successfully')) {
      builder.hasCompleted = true;
      builder.endTime = entry.time;
      builder.status = 'success';
      if (entry.output) {
        builder.output = String(entry.output);
      }
      if (entry.duration_ms) {
        builder.durationMs = Number(entry.duration_ms);
      }

      if (shouldIncludeInCompletedTraces(builder)) {
        completedTraces.push(builder);
      }
    }

    // Check if this is failed execution (失败日志)
    // 从独立字段读取 error, duration_ms
    if (message.includes('execution failed')) {
      builder.hasCompleted = true;
      builder.endTime = entry.time;
      builder.status = 'failed';
      if (entry.error) {
        builder.error = { message: String(entry.error) };
      }
      if (entry.duration_ms) {
        builder.durationMs = Number(entry.duration_ms);
      }

      if (shouldIncludeInCompletedTraces(builder)) {
        completedTraces.push(builder);
      }
    }
  };

  const processLogEntry = (entry: LogEntry): void => {
    const { trace_id: traceId, capability_id: capId } = entry;

    if (!traceId || !capId || capId !== capabilityId) return;

    let builder = builders.get(traceId);
    if (!builder) {
      builder = createCapabilityTraceBuilder(traceId, capId);
      builders.set(traceId, builder);
    }

    updateBuilderMetadata(builder, entry);
  };

  const processLine = (line: string): void => {
    const entry = parseLogLine(line);
    if (entry?.capability_id) {
      processLogEntry(entry);
    }
  };

  await readFileReverse(filePath, config.chunkSize, processLine);

  // Sort by endTime descending (newest first)
  completedTraces.sort((a, b) => {
    const timeA = a.endTime ? new Date(a.endTime).getTime() : 0;
    const timeB = b.endTime ? new Date(b.endTime).getTime() : 0;
    return timeB - timeA;
  });

  // Apply limit after sorting
  const limitedTraces = limit ? completedTraces.slice(0, limit) : completedTraces;

  return {
    capabilityId,
    totalTraces: limitedTraces.length,
    traces: limitedTraces.map((builder): CapabilityTraceItem => ({
      traceId: builder.traceId,
      capabilityId: builder.capabilityId,
      pluginKey: builder.pluginKey,
      action: builder.action,
      startTime: builder.startTime,
      endTime: builder.endTime,
      durationMs: builder.durationMs,
      status: builder.status || 'failed',
      input: builder.input,
      output: builder.output,
      error: builder.error,
    })),
  };
}
